import { Filter as FilterIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { cycleSortDir, DataTable, type Column } from "../../components/DataTable";
import { RowsPerCardSelect } from "../../components/RowsPerCardSelect";
import { SearchInput } from "../../components/SearchInput";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { BlurredSheetContent } from "../itCompliance/components/BlurredSheetContent";
import { DrilldownSheetHeader } from "../itCompliance/components/DrilldownSheetHeader";
import { FilterDropdown } from "../itCompliance/components/FilterDropdown";
import { TOOLBAR_BUTTON_CLASS } from "../itCompliance/constants";
import { getAssetCoverageScoreColumns } from "./columns";
import {
  fetchAssetCoverageOnboardingAssets,
  fetchAssetCoverageScoreAssets,
  type AssetCoverageScoreRow,
  type AssetCoverageSeverity,
} from "./services";

export type AssetCoverageDrilldownTarget =
  | { kind: "onboarding"; status: "onboarded" | "not-onboarded" }
  | { kind: "severity"; severity: AssetCoverageSeverity };

export interface DrilldownFilterOptions {
  applications: string[];
  tools: string[];
  environments: string[];
  types: string[];
  statuses: string[];
}

type DrilldownFilterSelection = Record<keyof DrilldownFilterOptions, string[]>;

const EMPTY_SELECTION: DrilldownFilterSelection = {
  applications: [],
  tools: [],
  environments: [],
  types: [],
  statuses: [],
};

const sortedOptions = (opts: string[]): string[] =>
  [...opts].sort((a, b) => a.localeCompare(b));

// Alphabetical by label, matching the main table's filters drawer.
const FILTER_FIELDS: { key: keyof DrilldownFilterOptions; label: string }[] = [
  { key: "applications", label: "Application" },
  { key: "environments", label: "Environment" },
  { key: "statuses", label: "Onboarding Status" },
  { key: "tools", label: "Tool" },
  { key: "types", label: "Type" },
];

interface OnboardingDrilldownDrawerProps {
  /** null = closed. */
  target: AssetCoverageDrilldownTarget | null;
  onClose: () => void;
  applicationNames: string[];
  tools: string[];
  /** Options for the Filters drawer; same lists as the page's table filters. */
  filterOptions: DrilldownFilterOptions;
  /** Bumped by the page on Filter/Clear so an open drawer refetches. */
  refreshKey: number;
}

function drilldownTitle(target: AssetCoverageDrilldownTarget | null): string {
  if (!target) return "";
  if (target.kind === "severity") return `${target.severity} Assets By Asset Score`;
  return target.status === "not-onboarded" ? "Not Onboarded Assets" : "Onboarded Assets";
}

export function OnboardingDrilldownDrawer({
  target,
  onClose,
  applicationNames,
  tools,
  filterOptions,
  refreshKey,
}: OnboardingDrilldownDrawerProps) {
  // An empty applied list = untouched (page-level scope applies). Applying keeps the list as chosen,
  // even when everything is checked, so apps outside the page-level scope are still sent.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [applied, setApplied] = useState<DrilldownFilterSelection>(EMPTY_SELECTION);
  const [draft, setDraft] = useState<DrilldownFilterSelection>(EMPTY_SELECTION);
  const selectAll = (): DrilldownFilterSelection => ({ ...filterOptions });
  // The onboarded / not-onboarded cards are already scoped by onboarding status.
  const hideStatusFilter = target?.kind === "onboarding";
  const visibleFields = hideStatusFilter
    ? FILTER_FIELDS.filter(({ key }) => key !== "statuses")
    : FILTER_FIELDS;
  const appliedKey = FILTER_FIELDS.map(({ key }) => applied[key].join(",")).join("|");
  const filterCount = visibleFields.filter(
    ({ key }) => applied[key].length > 0 && applied[key].length < filterOptions[key].length,
  ).length;

  const [rows, setRows] = useState<AssetCoverageScoreRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const targetKey = target ? (target.kind === "severity" ? target.severity : target.status) : "";

  // Fresh paging/search/sort each time a card is opened
  useEffect(() => {
    setPage(0);
    setSearchInput("");
    setSearch("");
    setSortKey(null);
    setSortDir("asc");
    setApplied(EMPTY_SELECTION);
    setFiltersOpen(false);
    setRows([]);
    setTotal(0);
  }, [targetKey]);

  const applicationKey = applicationNames.join(",");
  const toolKey = tools.join(",");

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    setLoading(true);
    const body = {
      applicationNames: applied.applications.length > 0 ? applied.applications : applicationNames,
      tools: applied.tools.length > 0 ? applied.tools : tools,
      environments: applied.environments.length > 0 ? applied.environments : undefined,
      types: applied.types.length > 0 ? applied.types : undefined,
      toolStatus:
        target.kind !== "onboarding" && applied.statuses.length > 0 ? applied.statuses : undefined,
      search: search.trim() || undefined,
      page: page + 1,
      pageSize,
      sortKey: (sortKey as "name" | "type" | "ip" | "environment" | "applications" | "assetScore") ?? undefined,
      sortDir,
    };
    const request =
      target.kind === "severity"
        ? fetchAssetCoverageScoreAssets({ ...body, severity: target.severity })
        : fetchAssetCoverageOnboardingAssets(target.status, body);
    request
      .then((data) => {
        if (cancelled) return;
        setRows((data?.items ?? []) as AssetCoverageScoreRow[]);
        setTotal(data?.total ?? 0);
      })
      .catch((err: unknown) => {
        console.error("Asset coverage drilldown fetch failed:", err);
        if (!cancelled) {
          setRows([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, applicationKey, toolKey, appliedKey, search, page, pageSize, sortKey, sortDir, refreshKey]);

  const columns = useMemo(
    () => [
      {
        key: "rowNumber",
        header: "#",
        align: "center",
        sortable: false,
        width: "60px",
        minWidth: 50,
        render: (_row: AssetCoverageScoreRow, index: number) => (
          <span className="text-xs font-normal text-foreground tabular-nums">{page * pageSize + index + 1}</span>
        ),
      } as Column<AssetCoverageScoreRow>,
      ...getAssetCoverageScoreColumns(rows, target?.kind === "severity"),
    ],
    [target?.kind, rows, page, pageSize],
  );

  return (
    <Sheet open={target !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex h-full max-h-screen w-[90vw] max-w-none flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-none"
      >
        <DrilldownSheetHeader title={drilldownTitle(target)} />
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              value={searchInput}
              onChange={setSearchInput}
              onSubmit={(value) => {
                setSearch(value);
                setPage(0);
              }}
              placeholder="Search"
              className="flex-1 min-w-[200px] max-w-sm"
            />
            <button
              type="button"
              onClick={() => {
                setDraft({
                  applications: applied.applications.length ? applied.applications : filterOptions.applications,
                  tools: applied.tools.length ? applied.tools : filterOptions.tools,
                  environments: applied.environments.length ? applied.environments : filterOptions.environments,
                  types: applied.types.length ? applied.types : filterOptions.types,
                  statuses: applied.statuses.length ? applied.statuses : filterOptions.statuses,
                });
                setFiltersOpen(true);
              }}
              className={TOOLBAR_BUTTON_CLASS}
            >
              <FilterIcon className="h-4 w-4" />
              Filters
              {filterCount > 0 && (
                <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-xs font-semibold text-primary">
                  {filterCount}
                </span>
              )}
            </button>
            <div className="ml-auto flex flex-shrink-0 items-center gap-2">
              <RowsPerCardSelect
                value={pageSize}
                onChange={(n) => {
                  setPageSize(n);
                  setPage(0);
                }}
                label="Rows per page"
              />
            </div>
          </div>
          <DataTable
            maxHeight="500px"
            columns={columns}
            data={rows}
            emptyText={loading ? "Loading..." : "No assets found"}
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={setPage}
            itemLabel="assets"
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={(key: string) => {
              const next = cycleSortDir(sortKey, sortDir, key);
              setSortKey(next.sortKey);
              setSortDir(next.sortDir);
              setPage(0);
            }}
          />
        </div>

        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <BlurredSheetContent
            side="right"
            overlayClassName="bg-transparent backdrop-blur-none"
            className="flex h-full max-h-screen w-[min(50vw,42rem)] flex-col gap-0 border-l border-border bg-card p-0 max-sm:w-full"
          >
            <SheetHeader className="gap-1 border-b border-border px-3 py-2">
              <SheetTitle className="text-base font-semibold">Select Filters</SheetTitle>
            </SheetHeader>
            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4">
              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => setDraft(selectAll())}
                  className="text-sm font-semibold text-blue-600 underline-offset-2 hover:underline dark:text-blue-400"
                >
                  Reset Filters
                </button>
              </div>
              {visibleFields.map(({ key, label }) => (
                <FilterDropdown
                  key={key}
                  label={label}
                  options={sortedOptions(filterOptions[key])}
                  value={draft[key]}
                  onChange={(next: string[]) => setDraft((d) => ({ ...d, [key]: next }))}
                  searchPlaceholder="Search"
                  disabled={filterOptions[key].length === 0}
                  size="sm"
                  surface="input"
                  variant="pill"
                  className="w-full px-4"
                  modal
                />
              ))}
            </div>
            <SheetFooter className="flex-row justify-between border-t border-border">
              <button
                type="button"
                onClick={() => {
                  setDraft(selectAll());
                  setApplied(EMPTY_SELECTION);
                  setPage(0);
                  setFiltersOpen(false);
                }}
                className="rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-accent/60"
              >
                Clear and Close
              </button>
              <button
                type="button"
                onClick={() => {
                  setApplied(draft);
                  setPage(0);
                  setFiltersOpen(false);
                }}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                Apply Filters
              </button>
            </SheetFooter>
          </BlurredSheetContent>
        </Sheet>
      </SheetContent>
    </Sheet>
  );
}
